/**
 * useFormSave Hook
 * Manages form save strategy and saving state
 */

import { useCallback, useMemo, useState } from 'react';
import { FormContainerConfig, ISaveStrategy } from '../types/index';
import { SaveStrategyFactory } from '../strategies/SaveStrategies';
import { useMasterDataStore } from '../stores/MasterDataStore';
import { useEventManagerStore } from '../stores/EventManagerStore';

export interface UseFormSaveReturn {
  strategy: ISaveStrategy;
  isSaving: boolean;
  save: (formID: string) => Promise<void>;
  saveAll: () => Promise<void>;
}

/**
 * Hook for managing form save
 * Handles:
 * - Creating save strategy from config
 * - Tracking save state
 * - Saving individual or all forms
 */
export function useFormSave(config: FormContainerConfig): UseFormSaveReturn {
  const [isSaving, setIsSaving] = useState(false);
  const masterData = useMasterDataStore();
  const eventManager = useEventManagerStore();

  // Create save strategy based on config
  const strategy = useMemo(() => {
    const firstChild = config.childForms[0];
    const apiUrl = firstChild?.dataBinding.saveTarget === 'api'
      ? (firstChild.dataBinding as any).apiUrl
      : undefined;
    return SaveStrategyFactory.createStrategy(
      firstChild?.dataBinding.saveTarget || 'none',
      { apiUrl }
    );
  }, [config]);

  // Save a single form's data
  const save = useCallback(
    async (formID: string) => {
      setIsSaving(true);
      try {
        const canSave = await strategy.canSave(formID);
        if (!canSave) {
          return;
        }

        const formData = masterData.getData();
        await strategy.save(formID, formData);

        // Emit save event
        eventManager.emit('form:saved', { formID, data: formData });
      } catch (error) {
        strategy.onSaveError(error as Error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [strategy, masterData, eventManager]
  );

  // Save all forms
  const saveAll = useCallback(async () => {
    setIsSaving(true);
    try {
      const allData = masterData.getData();

      // Save each child form
      for (const childForm of config.childForms) {
        const canSave = await strategy.canSave(childForm.formID);
        if (canSave) {
          await strategy.save(childForm.formID, allData);
        }
      }

      // Emit save event
      eventManager.emit('form:saved', { data: allData });
    } catch (error) {
      strategy.onSaveError(error as Error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [strategy, config, masterData, eventManager]);

  return {
    strategy,
    isSaving,
    save,
    saveAll,
  };
}
